/**
 * Supabase Database Integration
 * Handles customer data storage and tracking
 */

import { createClient } from '@supabase/supabase-js';

// TODO: Replace with your Supabase credentials
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://placeholder.supabase.co';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'placeholder_key';

// Check if Supabase is properly configured
const isSupabaseConfigured =
  supabaseUrl &&
  supabaseAnonKey &&
  !supabaseUrl.includes('placeholder') &&
  !supabaseAnonKey.includes('placeholder');

// Only create client if properly configured
export const supabase = isSupabaseConfigured
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null;

/**
 * SQL Schema for Supabase:
 *
 * CREATE TABLE customer_submissions (
 *   id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
 *   created_at TIMESTAMP DEFAULT NOW(),
 *   first_name TEXT NOT NULL,
 *   last_name TEXT NOT NULL,
 *   email TEXT NOT NULL,
 *   opt_in_surveys BOOLEAN DEFAULT FALSE,
 *   review_generated BOOLEAN DEFAULT FALSE,
 *   review_stars INTEGER,
 *   review_tone TEXT,
 *   review_text TEXT,
 *   went_to_amazon BOOLEAN DEFAULT FALSE,
 *   claimed_gifts BOOLEAN DEFAULT FALSE
 * );
 *
 * -- Enable Row Level Security
 * ALTER TABLE customer_submissions ENABLE ROW LEVEL SECURITY;
 *
 * -- Policy to allow inserting
 * CREATE POLICY "Allow public insert" ON customer_submissions
 *   FOR INSERT WITH CHECK (true);
 *
 * -- Policy to allow updating (optional, for security consider limiting this)
 * CREATE POLICY "Allow update own record" ON customer_submissions
 *   FOR UPDATE USING (true);
 */

/**
 * Create initial customer submission
 * @param {Object} customerData - Customer information
 * @returns {Promise<string>} - Submission ID
 */
export async function createSubmission(customerData) {
  // Demo mode - Supabase not configured
  if (!supabase) {
    console.warn('Supabase not configured. Running in demo mode.');
    return 'demo-' + Math.random().toString(36).substr(2, 9);
  }

  const { data, error } = await supabase
    .from('customer_submissions')
    .insert([
      {
        first_name: customerData.firstName,
        last_name: customerData.lastName,
        email: customerData.email,
        opt_in_surveys: customerData.optInSurveys || false,
        region: 'US'
      }
    ])
    .select()
    .single();

  if (error) {
    console.error('Error creating submission:', error);
    throw new Error('Failed to save customer information');
  }

  return data.id;
}

/**
 * Update submission with review data
 * @param {string} id - Submission ID
 * @param {Object} reviewData - Review information (stars, tone, reviewText)
 */
export async function updateReviewData(id, reviewData) {
  if (!supabase) {
    console.warn('Supabase not configured. Skipping review data update.');
    return;
  }

  const { error } = await supabase
    .from('customer_submissions')
    .update({
      review_generated: true,
      review_stars: reviewData.stars,
      review_tone: reviewData.tone,
      review_text: reviewData.reviewText || null
    })
    .eq('id', id);

  if (error) {
    console.error('Error updating review data:', error);
    throw new Error('Failed to save review data');
  }
}

/**
 * Track when user goes to Amazon
 * @param {string} id - Submission ID
 */
export async function trackAmazonVisit(id) {
  if (!supabase) {
    console.warn('Supabase not configured. Skipping Amazon visit tracking.');
    return;
  }

  const { error } = await supabase
    .from('customer_submissions')
    .update({ went_to_amazon: true })
    .eq('id', id);

  if (error) {
    console.error('Error tracking Amazon visit:', error);
  }
}

/**
 * Track when user claims gifts
 * @param {string} id - Submission ID
 */
export async function trackGiftsClaimed(id) {
  if (!supabase) {
    console.warn('Supabase not configured. Skipping gifts claimed tracking.');
    return;
  }

  const { error } = await supabase
    .from('customer_submissions')
    .update({ claimed_gifts: true })
    .eq('id', id);

  if (error) {
    console.error('Error tracking gifts claimed:', error);
  }
}

/**
 * Track individual gift download
 * @param {string} customerId - Customer submission ID
 * @param {string} giftType - Type of gift (spotify_playlist, game_guide, relationship_ebook)
 */
export async function trackGiftDownload(customerId, giftType) {
  if (!supabase) {
    console.warn('Supabase not configured. Skipping gift download tracking.');
    return;
  }

  const { error} = await supabase
    .from('gift_downloads')
    .insert({
      customer_id: customerId,
      gift_type: giftType,
      region: 'US'
    });

  if (error) {
    console.error('Error tracking gift download:', error);
  }
}
